import { useState, useRef, useEffect } from 'react';
import { TrendingUp, TrendingDown, ChevronDown, ChevronLeft, ChevronRight, ArrowUpRight, ArrowDownRight, Calendar, FileText } from 'lucide-react';
import { Card } from '@components/ui';
import AyKarti from './AyKarti';

/**
 * MonthlyDetail - Aylık Gelir/Gider Detay Görünümü
 * Premium tasarım: Glassmorphism kartlar, drag-to-scroll, ok navigasyonu
 */
export default function MonthlyDetail({ monthlyData = [], selectedYear, formatCurrency }) {
    const [selectedMonthIndex, setSelectedMonthIndex] = useState(0);


    // Drag-to-scroll state (exactly like Invoice component)
    const scrollRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);
    const [showLeftArrow, setShowLeftArrow] = useState(false);
    const [showRightArrow, setShowRightArrow] = useState(true);

    // Filter months by selected year
    const filteredMonths = monthlyData.filter(m => m.month.startsWith(selectedYear));

    // Set initial selected month when year changes
    useEffect(() => {
        if (filteredMonths.length > 0) {
            setSelectedMonthIndex(filteredMonths.length - 1);
        }
    }, [selectedYear, filteredMonths.length]);


    // Drag handlers with console logs for debugging
    const handleMouseDown = (e) => {
        setIsDragging(true);
        setStartX(e.pageX - scrollRef.current.offsetLeft);
        setScrollLeft(scrollRef.current.scrollLeft);
    };

    const handleMouseMove = (e) => {
        if (!isDragging) return;
        const x = e.pageX - scrollRef.current.offsetLeft;
        const walk = (x - startX) * 1.5;
        scrollRef.current.scrollLeft = scrollLeft - walk;
        updateArrows();
    };

    const handleMouseUp = () => setIsDragging(false);



    // Arrow visibility update
    const updateArrows = () => {
        const el = scrollRef.current;
        if (!el) return;
        setShowLeftArrow(el.scrollLeft > 0);
        setShowRightArrow(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    };

    useEffect(() => {
        if (!scrollRef.current) return;
        updateArrows();
        const scrollEl = scrollRef.current;
        scrollEl.addEventListener('scroll', updateArrows);
        window.addEventListener('resize', updateArrows);
        return () => {
            scrollEl.removeEventListener('scroll', updateArrows);
            window.removeEventListener('resize', updateArrows);
        };
    }, [filteredMonths]);

    const scrollToDirection = (direction) => {
        if (!scrollRef.current) return;
        const scrollAmount = direction === 'left' ? -300 : 300;
        scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    };

    if (!monthlyData || monthlyData.length === 0) {


        return (
            <div className="text-center py-16 text-text-tertiary">
                <Calendar size={56} className="mx-auto mb-4 opacity-40" />
                <p className="text-lg">Aylık veri bulunamadı.</p>
            </div>
        );
    }

    const selectedMonth = filteredMonths[selectedMonthIndex] || filteredMonths[filteredMonths.length - 1];
    const netBalance = (selectedMonth?.income || 0) - (selectedMonth?.expense || 0);

    // Month names
    const monthNames = {
        '01': 'Ocak', '02': 'Şubat', '03': 'Mart', '04': 'Nisan',
        '05': 'Mayıs', '06': 'Haziran', '07': 'Temmuz', '08': 'Ağustos',
        '09': 'Eylül', '10': 'Ekim', '11': 'Kasım', '12': 'Aralık'
    };
    const monthShort = {
        '01': 'Oca', '02': 'Şub', '03': 'Mar', '04': 'Nis',
        '05': 'May', '06': 'Haz', '07': 'Tem', '08': 'Ağu',
        '09': 'Eyl', '10': 'Eki', '11': 'Kas', '12': 'Ara'
    };

    const getMonthNum = (monthStr) => monthStr.split('-')[1];
    const getMonthName = (monthStr) => monthNames[getMonthNum(monthStr)] || monthStr;
    const getMonthShort = (monthStr) => monthShort[getMonthNum(monthStr)] || monthStr;

    // Format helpers
    const formatCompact = (amount) => {
        if (Math.abs(amount) >= 1000000) return `${(amount / 1000000).toFixed(1)}M`;
        if (Math.abs(amount) >= 1000) return `${Math.round(amount / 1000)}K`;
        return formatCurrency(amount).replace('₺', '');
    };

    const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });

    // Category labels
    const incomeCategoryLabels = { apartmentSales: 'Daire Satışı', installments: 'Taksitler', kapora: 'Kapora', other: 'Diğer' };
    const expenseCategoryLabels = { materials: 'Malzeme', labor: 'İşçilik', infrastructure: 'Altyapı', permits: 'İzinler', utilities: 'Faturalar', other: 'Diğer' };

    // Handler for card click - same pattern as InvoiceMonthCard
    const handleMonthClick = (index) => {
        setSelectedMonthIndex(index);
    };


    // Category Bar Component
    const CategoryBar = ({ label, value, total, variant }) => {
        const pct = total ? Math.round((value / total) * 100) : 0;
        const barColor = variant === 'income' ? 'bg-success' : 'bg-secondary';

        return (
            <div className="flex items-center gap-3">
                <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-center mb-1">
                        <span className="text-sm text-text-secondary truncate">{label}</span>
                        <span className="text-sm font-semibold text-text-primary ml-2">₺{formatCompact(value)}</span>
                    </div>
                    <div className="h-1.5 bg-neutral-100 rounded-full overflow-hidden">
                        <div className={`h-full ${barColor} rounded-full transition-all duration-500`} style={{ width: `${pct}%` }} />
                    </div>
                </div>
                <span className="text-xs text-text-muted w-8 text-right">%{pct}</span>
            </div>
        );
    };

    // Transaction Card Component
    const TransactionCard = ({ transaction }) => (
        <div className="flex items-center gap-4 py-4 border-b border-border-secondary last:border-0 hover:bg-bg-tertiary/50 -mx-2 px-2 rounded-lg transition-colors">
            <div className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${transaction.type === 'income' ? 'bg-success/10' : 'bg-secondary/10'
                }`}>
                {transaction.type === 'income' ? (
                    <ArrowUpRight size={22} className="text-success" />
                ) : (
                    <ArrowDownRight size={22} className="text-secondary" />
                )}
            </div>
            <div className="flex-1 min-w-0">
                <p className="font-medium text-text-primary truncate">{transaction.person}</p>
                <p className="text-sm text-text-muted truncate">{transaction.description}</p>
            </div>
            <div className="text-right shrink-0">
                <p className={`font-bold text-lg ${transaction.type === 'income' ? 'text-success' : 'text-secondary'}`}>
                    {transaction.type === 'income' ? '+' : '-'}₺{formatCompact(transaction.amount)}
                </p>
                <p className="text-xs text-text-muted">{formatDate(transaction.date)}</p>
            </div>
        </div>
    );

    // Category Breakdown
    const CategoryBreakdown = ({ categories, labels, total, variant }) => {
        if (!categories) return <p className="text-sm text-text-muted py-6 text-center">Kategori verisi yok</p>;

        const sorted = Object.entries(categories)
            .filter(([_, v]) => v > 0)
            .sort((a, b) => b[1] - a[1]);

        if (sorted.length === 0) return <p className="text-sm text-text-muted py-6 text-center">Kayıt yok</p>;

        return (
            <div className="space-y-4">
                {sorted.map(([key, value]) => (
                    <CategoryBar
                        key={key}
                        label={labels[key] || key}
                        value={value}
                        total={total}
                        variant={variant}
                    />
                ))}
            </div>
        );
    };

    return (
        <div className="space-y-6">
            {/* Month Cards - Horizontal Scroll with Drag & Arrows */}

            <div className="relative" style={{ overflow: 'visible' }}>
                {/* Left Arrow */}
                {showLeftArrow && (
                    <div
                        className="absolute left-0 top-1/2 -translate-y-1/2 z-10 cursor-pointer hidden md:flex"
                        onClick={() => scrollToDirection('left')}
                    >
                        <div className="w-11 h-11 rounded-full flex items-center justify-center shadow-xl bg-white/90 dark:bg-neutral-800/90 backdrop-blur-xl border border-white/30 hover:scale-110 transition-transform">
                            <ChevronLeft size={24} className="text-primary" />
                        </div>
                    </div>
                )}

                {/* Right Arrow */}
                {showRightArrow && (
                    <div
                        className="absolute right-0 top-1/2 -translate-y-1/2 z-10 cursor-pointer hidden md:flex"
                        onClick={() => scrollToDirection('right')}
                    >
                        <div className="w-11 h-11 rounded-full flex items-center justify-center shadow-xl bg-white/90 dark:bg-neutral-800/90 backdrop-blur-xl border border-white/30 hover:scale-110 transition-transform">
                            <ChevronRight size={24} className="text-primary" />
                        </div>
                    </div>
                )}

                {/* Scrollable Container with drag-to-scroll */}
                <div
                    ref={scrollRef}
                    className="flex gap-4 pb-4 cursor-grab active:cursor-grabbing"
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                    style={{ overflowX: 'auto', scrollbarWidth: 'none' }}
                >
                    <div className="flex gap-4 min-w-fit py-2 px-6 md:px-12">
                        {filteredMonths.map((month, idx) => (
                            <AyKarti
                                key={month.month}
                                month={month}
                                index={idx}
                                isSelected={idx === selectedMonthIndex}
                                onSelect={handleMonthClick}
                                formatCompact={formatCompact}
                                getMonthShort={getMonthShort}
                            />
                        ))}
                    </div>

                </div>

            </div>


            {/* Selected Month Header */}
            <div className="text-center pb-2">
                <h2 className="text-2xl font-bold text-text-primary">
                    {getMonthName(selectedMonth.month)} {selectedYear}
                </h2>
            </div>

            {/* Summary Stats - Glassmorphism */}
            <div className="grid grid-cols-3 gap-4">
                <div className="bg-white/60 dark:bg-white/10 backdrop-blur-xl border border-white/30 rounded-2xl p-5 text-center shadow-lg"
                    style={{ backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' }}>
                    <div className="flex items-center justify-center gap-2 mb-2">
                        <div className="w-8 h-8 rounded-full bg-success/10 flex items-center justify-center">
                            <TrendingUp size={18} className="text-success" />
                        </div>
                    </div>
                    <p className="text-xs text-text-muted mb-1">Gelir</p>
                    <p className="text-2xl font-bold text-text-primary">₺{formatCompact(selectedMonth.income)}</p>
                </div>

                <div className="bg-white/60 dark:bg-white/10 backdrop-blur-xl border border-white/30 rounded-2xl p-5 text-center shadow-lg"
                    style={{ backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' }}>
                    <div className="flex items-center justify-center gap-2 mb-2">
                        <div className="w-8 h-8 rounded-full bg-secondary/10 flex items-center justify-center">
                            <TrendingDown size={18} className="text-secondary" />
                        </div>
                    </div>
                    <p className="text-xs text-text-muted mb-1">Gider</p>
                    <p className="text-2xl font-bold text-text-primary">₺{formatCompact(selectedMonth.expense)}</p>
                </div>

                <div className={`bg-white/60 dark:bg-white/10 backdrop-blur-xl border rounded-2xl p-5 text-center shadow-lg ${netBalance >= 0 ? 'border-success/40' : 'border-secondary/40'
                    }`} style={{ backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' }}>
                    <div className="flex items-center justify-center gap-2 mb-2">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${netBalance >= 0 ? 'bg-success/10' : 'bg-secondary/10'
                            }`}>
                            {netBalance >= 0 ? (
                                <TrendingUp size={18} className="text-success" />
                            ) : (
                                <TrendingDown size={18} className="text-secondary" />
                            )}
                        </div>
                    </div>
                    <p className="text-xs text-text-muted mb-1">Net</p>
                    <p className={`text-2xl font-bold ${netBalance >= 0 ? 'text-success' : 'text-secondary'}`}>
                        {netBalance >= 0 ? '+' : ''}₺{formatCompact(netBalance)}
                    </p>
                </div>
            </div>

            {/* Category Distribution */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Card padding="lg">
                    <div className="flex items-center gap-2 mb-5 pb-3 border-b border-border-secondary">
                        <div className="w-3 h-3 rounded-full bg-success"></div>
                        <h3 className="font-semibold text-text-primary">Gelir Dağılımı</h3>
                        <span className="ml-auto text-sm font-bold text-success">₺{formatCompact(selectedMonth.income)}</span>
                    </div>
                    <CategoryBreakdown
                        categories={selectedMonth.incomeCategories}
                        labels={incomeCategoryLabels}
                        total={selectedMonth.income}
                        variant="income"
                    />
                </Card>

                <Card padding="lg">
                    <div className="flex items-center gap-2 mb-5 pb-3 border-b border-border-secondary">
                        <div className="w-3 h-3 rounded-full bg-secondary"></div>
                        <h3 className="font-semibold text-text-primary">Gider Dağılımı</h3>
                        <span className="ml-auto text-sm font-bold text-secondary">₺{formatCompact(selectedMonth.expense)}</span>
                    </div>
                    <CategoryBreakdown
                        categories={selectedMonth.expenseCategories}
                        labels={expenseCategoryLabels}
                        total={selectedMonth.expense}
                        variant="expense"
                    />
                </Card>
            </div>

            {/* Transactions */}
            <Card padding="lg">
                <div className="flex items-center gap-2 mb-4 pb-3 border-b border-border-secondary">
                    <FileText size={18} className="text-text-muted" />
                    <h3 className="font-semibold text-text-primary">Bu Aydaki İşlemler</h3>
                    <span className="ml-auto text-sm text-text-muted">
                        {selectedMonth.transactions?.length || 0} kayıt
                    </span>
                </div>

                {selectedMonth.transactions && selectedMonth.transactions.length > 0 ? (
                    <div>
                        {selectedMonth.transactions.map(tx => (
                            <TransactionCard key={tx.id} transaction={tx} />
                        ))}
                    </div>
                ) : (
                    <div className="py-12 text-center">
                        <Calendar size={48} className="mx-auto mb-3 text-text-muted/40" />
                        <p className="text-text-muted">Bu ay için işlem kaydı yok.</p>
                    </div>
                )}
            </Card>
        </div>
    );
}
